import type { ReactNode } from "react"
import { createHighlighter, type HighlightTokenClass } from "@tanstack/highlight/core"
import { css } from "@tanstack/highlight/languages/css"
import { diff } from "@tanstack/highlight/languages/diff"
import { html } from "@tanstack/highlight/languages/html"
import { js } from "@tanstack/highlight/languages/js"
import { json } from "@tanstack/highlight/languages/json"
import { jsx } from "@tanstack/highlight/languages/jsx"
import { markdown } from "@tanstack/highlight/languages/markdown"
import { python } from "@tanstack/highlight/languages/python"
import { shell } from "@tanstack/highlight/languages/shell"
import { sql } from "@tanstack/highlight/languages/sql"
import { ts } from "@tanstack/highlight/languages/ts"
import { tsx } from "@tanstack/highlight/languages/tsx"
import { yaml } from "@tanstack/highlight/languages/yaml"

const highlighter = createHighlighter({
  languages: [
    { ...shell, aliases: [...(shell.aliases ?? []), "shellscript"] },
    css,
    diff,
    html,
    js,
    json,
    jsx,
    markdown,
    python,
    sql,
    ts,
    tsx,
    yaml,
  ],
})

const tokenClassName: Record<HighlightTokenClass, string | undefined> = {
  attr: "text-badge-blue-fg-text",
  "code-inline": "text-badge-green-fg-text",
  command: "text-badge-blue-fg-text",
  comment: "text-fg-muted",
  deleted: "text-badge-red-fg-text",
  function: "text-badge-blue-fg-text",
  heading: undefined,
  inserted: "text-badge-green-fg-text",
  keyword: "text-badge-violet-fg-text",
  link: "text-fg-interactive",
  literal: "text-badge-blue-fg-text",
  meta: "text-badge-green-fg-text",
  number: "text-badge-blue-fg-text",
  operator: "text-badge-violet-fg-text",
  property: "text-badge-violet-fg-text",
  selector: "text-badge-blue-fg-text",
  string: "text-badge-green-fg-text",
  tag: "text-badge-blue-fg-text",
  type: "text-badge-blue-fg-text",
  variable: undefined,
}

/** `code` split into lines of highlighted spans. Unsupported languages come back as plain lines. */
export function highlightLines(code: string, language: string): ReactNode[][] {
  let line: ReactNode[] = []
  const lines = [line]
  const { tokens } = highlighter.tokenize(code, { lang: language })
  for (const [index, token] of tokens.entries()) {
    const className = token.className && tokenClassName[token.className]
    for (const [partIndex, part] of token.value.split(/\r?\n/).entries()) {
      if (partIndex > 0) {
        line = []
        lines.push(line)
      }
      if (!part) continue
      line.push(
        className ? (
          <span key={index} className={className}>
            {part}
          </span>
        ) : (
          part
        ),
      )
    }
  }
  return lines
}
